import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Activity, Palette, Check, ArrowRight, ArrowLeft, Users, Calendar, Flame, Store, Sparkles, GraduationCap, Loader2, Music, Utensils, PenTool } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { toast } from "sonner";

const PASSIONS = [
  { slug: "photography", title: "Photography", icon: Camera, img: "https://images.unsplash.com/photo-1429292394373-ddbcc6bb7468?w=600&q=80" },
  { slug: "fitness-wellness", title: "Fitness & Wellness", icon: Activity, img: "https://images.unsplash.com/photo-1602263515015-dd72f8e9f301?w=600&q=80" },
  { slug: "art-creative", title: "Art & Creative", icon: Palette, img: "https://images.unsplash.com/photo-1638788454421-bf9c0c4f3296?w=600&q=80" },
  { slug: "music", title: "Music", icon: Music, img: "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=600&q=80" },
  { slug: "cooking", title: "Cooking", icon: Utensils, img: "https://images.unsplash.com/photo-1466637574441-749b8f19452f?w=600&q=80" },
  { slug: "writing", title: "Writing", icon: PenTool, img: "https://images.unsplash.com/photo-1455390582262-044cdead277a?w=600&q=80" },
];

const GOALS = [
  { id: "find-people", label: "Find people", icon: Users },
  { id: "join-communities", label: "Join communities", icon: Users },
  { id: "attend-events", label: "Attend events", icon: Calendar },
  { id: "join-challenges", label: "Join challenges", icon: Flame },
  { id: "showcase", label: "Showcase my work", icon: Sparkles },
  { id: "build", label: "Build a community", icon: Users },
  { id: "monetize", label: "Monetize my passion", icon: Store },
  { id: "learn", label: "Learn & improve", icon: GraduationCap },
];

export default function Onboarding() {
  const { refresh, user } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [passions, setPassions] = useState([]);
  const [goals, setGoals] = useState([]);
  const [busy, setBusy] = useState(false);

  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const finish = async () => {
    setBusy(true);
    try {
      await api.put("/auth/onboarding", { passions, goals, skill_level: "beginner" });
      await refresh();
      toast.success("You're all set — welcome!");
      nav("/home", { replace: true });
    } catch {
      toast.error("Could not save. Please try again.");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFDF8] flex flex-col">
      <header className="h-16 flex items-center px-6 border-b border-[#071126]/8"><Logo /></header>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-2xl">
          <div className="flex items-center gap-2 mb-8">
            {[0, 1].map((s) => <span key={s} className={`h-1.5 rounded-full flex-1 transition-colors ${s <= step ? "bg-[#FF6B00]" : "bg-[#071126]/10"}`} />)}
          </div>

          {step === 0 && (
            <div className="pa-in">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#55708F] mb-2">Step 1 of 2</p>
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">What passions interest you?</h1>
              <p className="text-[#071126]/60 mt-2">Pick one or more. We'll personalize everything around them.</p>
              <div className="grid sm:grid-cols-3 gap-4 mt-8">
                {PASSIONS.map((p) => {
                  const on = passions.includes(p.slug);
                  return (
                    <button key={p.slug} onClick={() => toggle(passions, setPassions, p.slug)} data-testid={`onboard-passion-${p.slug}`}
                      className={`relative rounded-2xl overflow-hidden text-left transition-all pa-focus ${on ? "ring-2 ring-[#FF6B00] ring-offset-2" : "ring-1 ring-[#071126]/10"}`}>
                      <img src={p.img} alt="" className="h-32 w-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#071126]/80 to-transparent" />
                      {on && <span className="absolute top-3 right-3 h-7 w-7 rounded-full bg-[#FF6B00] grid place-items-center"><Check className="h-4 w-4 text-white" /></span>}
                      <div className="absolute bottom-3 left-3 flex items-center gap-2 text-white"><p.icon className="h-4 w-4" /><span className="font-semibold text-sm">{p.title}</span></div>
                    </button>
                  );
                })}
              </div>
              <div className="flex justify-end mt-10">
                <button disabled={passions.length === 0} onClick={() => setStep(1)} data-testid="onboard-next-btn"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF6B00] text-white font-semibold shadow-[0_6px_18px_rgba(255,107,0,0.28)] hover:bg-[#e05e00] disabled:opacity-40 transition-all pa-focus">
                  Continue<ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="pa-in">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#55708F] mb-2">Step 2 of 2</p>
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">What are you looking for?</h1>
              <p className="text-[#071126]/60 mt-2">Choose everything that sounds good. You can do it all on PassionAte.</p>
              <div className="grid sm:grid-cols-2 gap-3 mt-8">
                {GOALS.map((g) => {
                  const on = goals.includes(g.id);
                  return (
                    <button key={g.id} onClick={() => toggle(goals, setGoals, g.id)} data-testid={`onboard-goal-${g.id}`}
                      className={`flex items-center gap-3 p-4 rounded-xl border text-left transition-all pa-focus ${on ? "border-[#FF6B00] bg-[#FF6B00]/6" : "border-[#071126]/10 bg-white hover:border-[#071126]/25"}`}>
                      <span className={`h-9 w-9 rounded-lg grid place-items-center ${on ? "bg-[#FF6B00] text-white" : "bg-[#071126]/5 text-[#55708F]"}`}><g.icon className="h-4.5 w-4.5" /></span>
                      <span className="font-medium text-[#071126]">{g.label}</span>
                      {on && <Check className="h-4 w-4 text-[#FF6B00] ml-auto" />}
                    </button>
                  );
                })}
              </div>
              <div className="flex justify-between mt-10">
                <button onClick={() => setStep(0)} className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-[#071126]/70 font-semibold hover:bg-[#071126]/5 transition-all" data-testid="onboard-back-btn"><ArrowLeft className="h-4 w-4" />Back</button>
                <button onClick={finish} disabled={busy} data-testid="onboard-finish-btn"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF6B00] text-white font-semibold shadow-[0_6px_18px_rgba(255,107,0,0.28)] hover:bg-[#e05e00] disabled:opacity-60 transition-all pa-focus">
                  {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Enter PassionAte<ArrowRight className="h-4 w-4" /></>}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
